/**
 * Sector/Industry Classification service (Data Operations page, 4th
 * section, 2026-09-27, pivoted to manual CSV upload) — talks to
 * `/admin/sector-classification/*`. Backend source-of-truth for
 * `StockMaster.sector`/`industry`, populated from an admin-uploaded NSE
 * Indices CSV; see `app.services.stock_classification` for the underlying
 * pipeline. All 3 endpoints are admin-only (`require_roles("ADMIN")`),
 * same as the rest of the Data Operations page — no extra auth handling
 * needed here, `api` already attaches the bearer token.
 *
 * There is no automated sync anymore — niftyindices.com (Akamai-fronted)
 * silently tarpits non-browser requests in production, so an admin
 * downloads the CSV themselves and uploads it via `uploadCsv`, mirroring
 * `brokerAccountService.importDhanInstrumentMaster`'s multipart pattern.
 */

import api from "@/services/api";
import type {
  SectorClassificationCoverageResponse,
  SectorClassificationStatusResponse,
  SectorClassificationSyncResult,
} from "@/types";

export const sectorClassificationService = {
  /** `GET /admin/sector-classification/coverage` — read-only, never
   * triggers an upload. Total/classified/unclassified stocks, duplicate
   * mappings, unmapped source symbols, and which NSE hierarchy tiers are
   * actually available from the current source. */
  async getCoverage(): Promise<SectorClassificationCoverageResponse> {
    const { data } = await api.get<SectorClassificationCoverageResponse>(
      "/admin/sector-classification/coverage"
    );
    return data;
  },

  /** `GET /admin/sector-classification/status` — last upload's
   * status/timestamp. Read-only. */
  async getStatus(): Promise<SectorClassificationStatusResponse> {
    const { data } = await api.get<SectorClassificationStatusResponse>(
      "/admin/sector-classification/status"
    );
    return data;
  },

  /** `POST /admin/sector-classification/upload` — uploads a manually
   * downloaded NSE Indices index-constituent CSV (header row "Company
   * Name,Industry,Symbol,Series,ISIN Code"). Runs synchronously and
   * returns the full result directly (matched counts, unmapped symbols,
   * etc.) — see backend `app.api.v1.sector_classification` for why this is
   * safe in-request now that the slow external fetch is gone. A file that
   * fails to parse is rejected before any DB write; existing
   * classifications are never erased. */
  async uploadCsv(file: File): Promise<SectorClassificationSyncResult> {
    const formData = new FormData();
    formData.append("file", file);
    const { data } = await api.post<SectorClassificationSyncResult>(
      "/admin/sector-classification/upload",
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return data;
  },
};

export default sectorClassificationService;
